import CommunityPhotoCategoryFilter from '@/components/CommunityPhotoCategoryFilter/CommunityPhotoCategoryFilter';
import CommunityPhotosGrid from '@/components/CommunityPhotosGrid/CommunityPhotosGrid';
import Gallery from '@/components/Gallery/Gallery';
import Hero from '@/components/Hero/Hero';
import { useAppContext } from '@/contexts/AppContext';
import { EntityType } from '@/domain/entities';
import { useCommunityPhotos } from '@/hooks/community-photos';
import Spin from 'antd/es/spin';
import { type FC, useMemo, useState } from 'react';

const GalleryPage: FC = () => {
  const { community } = useAppContext();
  const { data: communityPhotos, isFetching: loadingPhotos } = useCommunityPhotos.useSuspenseQuery([
    community.id,
  ]);
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>();

  const categories = useMemo(
    () =>
      (communityPhotos ?? []).reduce<string[]>((acc, photo) => {
        photo.fileEntities
          ?.filter((entity) => entity.entityType === EntityType.COMMUNITY)
          .map((entity) => entity.metadata?.category)
          .forEach((category) => {
            if (category && !acc.includes(category)) {
              acc.push(category);
            }
          });
        return acc;
      }, []),
    [communityPhotos],
  );

  const filteredPhotos = useMemo(() => {
    if (!selectedCategory) {
      return communityPhotos || [];
    }

    return (
      communityPhotos?.filter((photo) =>
        photo.fileEntities
          ?.filter((entity) => entity.entityType === EntityType.COMMUNITY)
          .map((entity) => entity.metadata?.category)
          .some((category) => category === selectedCategory),
      ) ?? []
    );
  }, [selectedCategory, communityPhotos]);

  if (loadingPhotos) {
    return <Spin />;
  }

  return (
    <div>
      <Hero
        title="Community Gallery"
        subtitle="Explore our beautiful homes, amenities, and community spaces"
        showCTA={false}
      />
      <section className="py-16 bg-gray-50">
        {!communityPhotos || !categories || communityPhotos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No images found in this category</p>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CommunityPhotoCategoryFilter
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />

            <CommunityPhotosGrid
              images={filteredPhotos}
              setSelectedImageIndex={setSelectedImageIndex}
            />
          </div>
        )}
      </section>

      <Gallery
        images={filteredPhotos}
        selectedImageIndex={selectedImageIndex}
        setSelectedImageIndex={setSelectedImageIndex}
      />
    </div>
  );
};

export default GalleryPage;
